import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, ArrowRight, CheckCheck, ChevronDown } from '../../ui/Icon';
import { fxApi } from '../../../api/fx.api';
import { currencyOptions } from '../../../utils/currencies';
import type { ExpenseDebt } from '../../../types';

function Avatar({ name }: { name: string }) {
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['bg-violet-100 text-violet-700', 'bg-teal-100 text-teal-700', 'bg-amber-100 text-amber-700', 'bg-sky-100 text-sky-700', 'bg-rose-100 text-rose-700'];
  const color = colors[name.charCodeAt(0) % colors.length];
  return (
    <div className={`w-7 h-7 rounded-full ${color} flex items-center justify-center text-[11px] font-bold shrink-0`}>
      {initials}
    </div>
  );
}

/** Expandable settle panel: shows the debt in the base currency and, optionally,
 *  its equivalent in another currency (with the applied rate) if the debtor pays
 *  in cash of a different currency. */
function SettlePanel({ debt, onConfirm, onCancel }: {
  debt: ExpenseDebt;
  onConfirm: (note?: string | null) => void;
  onCancel: () => void;
}) {
  const { t, i18n } = useTranslation();
  const options = useMemo(() => currencyOptions(i18n.language), [i18n.language]);
  const [payCurrency, setPayCurrency] = useState(debt.currency);
  const [rate, setRate]       = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(false);

  const converts = payCurrency !== debt.currency;
  const equivalent = converts && rate ? Number(debt.amount) * rate : null;

  useEffect(() => {
    if (!converts) { setRate(null); setError(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(false);
    fxApi.rate(debt.currency, payCurrency)
      .then(r => { if (!cancelled) setRate(r.rate); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [payCurrency, debt.currency, converts]);

  function confirm() {
    const note = converts && equivalent != null
      ? t('expenses.settlement.paidAs', { amount: equivalent.toFixed(2), currency: payCurrency })
      : undefined;
    onConfirm(note);
  }

  return (
    <div className="basis-full w-full mt-1 mb-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[11px] tracking-[0.08em] uppercase text-slate-500 dark:text-slate-400">
          {t('expenses.settlement.payIn')}
        </span>
        <select
          value={payCurrency}
          onChange={e => setPayCurrency(e.target.value)}
          className="input py-1 text-xs w-40"
        >
          {options.map(c => <option key={c.code} value={c.code}>{c.code} · {c.name}</option>)}
        </select>
      </div>

      {converts && (
        <div className="text-xs text-slate-600 dark:text-slate-300 space-y-0.5">
          {loading && <p className="text-slate-400">{t('expenses.form.conversion.loading')}</p>}
          {error && <p className="text-amber-600 dark:text-amber-400">{t('expenses.settlement.rateUnavailable')}</p>}
          {!loading && !error && rate != null && (
            <>
              <p className="font-mono font-bold text-sm text-slate-900 dark:text-slate-100">
                {Number(debt.amount).toFixed(2)} {debt.currency} = {equivalent?.toFixed(2)} {payCurrency}
              </p>
              <p className="text-[11px] text-slate-400">
                1 {debt.currency} = {rate.toFixed(4)} {payCurrency}
              </p>
            </>
          )}
        </div>
      )}

      <div className="flex gap-2 justify-end pt-0.5">
        <button
          onClick={onCancel}
          className="px-2.5 py-1 rounded-lg text-[11px] font-semibold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={confirm}
          disabled={converts && (loading || error)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white text-[11px] font-semibold cursor-pointer"
        >
          <Icon as={CheckCheck} size={11} />
          {t('expenses.settlement.markSettled')}
        </button>
      </div>
    </div>
  );
}

function DebtRow({ debt, settled, onSettle }: { debt: ExpenseDebt; settled: boolean; onSettle?: (id: string, note?: string | null) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const debtorName = debt.debtor.displayName || debt.debtor.username;
  const creditorName = debt.creditor.displayName || debt.creditor.username;

  const nameClass = `text-xs font-medium truncate ${settled ? 'line-through text-slate-500 dark:text-slate-400' : 'text-slate-700 dark:text-slate-200'}`;

  // Wraps instead of using a fixed column template: on a phone the two names
  // plus the amount and the settle button don't fit on one line, and a grid
  // squeezed them until every name was an ellipsis. Below `sm` the pair takes
  // the full width and the amount/action drop to a second line.
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
      {/* Debtor → creditor */}
      <div className="flex items-center gap-2 min-w-0 basis-full sm:basis-0 sm:flex-1">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Avatar name={debtorName} />
          <span className={nameClass}>{debtorName}</span>
        </div>
        <Icon as={ArrowRight} size={12} className="text-slate-300 dark:text-slate-600 shrink-0" />
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Avatar name={creditorName} />
          <span className={nameClass}>{creditorName}</span>
        </div>
      </div>

      {/* Amount + action. On its own line below `sm`, spread edge to edge —
          hugging the right leaves a void under the names and reads as a
          stray fragment rather than the rest of the row. */}
      <div className="flex items-center gap-3 shrink-0 w-full justify-between sm:w-auto sm:justify-end sm:ml-auto">
        <span className={`font-mono text-sm font-bold tabular-nums tracking-tight whitespace-nowrap ${settled ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-900 dark:text-slate-100'}`}>
          <span className="text-[10px] font-normal mr-0.5 text-slate-400">{debt.currency}</span>
          {Number(debt.amount).toFixed(2)}
        </span>

        {settled ? (
          <Icon as={CheckCheck} size={14} className="text-teal-400" />
        ) : (
          <button
            onClick={() => setOpen(o => !o)}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-teal-500 hover:bg-teal-600 text-white text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <Icon as={CheckCheck} size={10} />
            {t('expenses.settlement.settle')}
          </button>
        )}
      </div>

      {open && !settled && (
        <SettlePanel
          debt={debt}
          onConfirm={note => { setOpen(false); onSettle?.(debt.id, note); }}
          onCancel={() => setOpen(false)}
        />
      )}
    </div>
  );
}

interface Props {
  debts: ExpenseDebt[];
  onSettle: (id: string, note?: string | null) => void;
}

export function Balances({ debts, onSettle }: Props) {
  const { t } = useTranslation();
  const unsettled = debts.filter(d => !d.settled);
  const settled = debts.filter(d => d.settled);
  const [settledOpen, setSettledOpen] = useState(false);

  if (debts.length === 0) {
    return (
      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-card">
        <p className="font-mono text-xs tracking-[0.1em] uppercase text-slate-500 dark:text-slate-400 mb-4">{t('expenses.whoOwesWhom')}</p>
        <div className="flex items-center gap-2 py-3">
          <Icon as={CheckCheck} size={16} className="text-teal-500 shrink-0" />
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('expenses.settlement.allEven')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-card">
      <p className="font-mono text-xs tracking-[0.1em] uppercase text-slate-500 dark:text-slate-400 mb-1">{t('expenses.whoOwesWhom')}</p>

      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {unsettled.map(debt => (
          <DebtRow key={debt.id} debt={debt} settled={false} onSettle={onSettle} />
        ))}
      </div>

      {settled.length > 0 && (
        <div className="mt-2 border-t border-slate-100 dark:border-slate-800 pt-2">
          <button
            onClick={() => setSettledOpen(o => !o)}
            className="flex items-center gap-1.5 w-full text-left cursor-pointer py-2"
          >
            <p className="font-mono text-[11px] tracking-[0.1em] uppercase text-slate-400 dark:text-slate-500 flex-1">
              {t('expenses.settledCount', { count: settled.length })}
            </p>
            <Icon
              as={ChevronDown}
              size={12}
              className={`text-slate-300 transition-transform ${settledOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {settledOpen && (
            <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-60 overflow-y-auto">
              {settled.map(debt => (
                <DebtRow key={debt.id} debt={debt} settled={true} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
